import axios from 'axios';
import { COUNTRY_LANGUAGE_MAP, getLanguageForCountryCode } from './countryLanguage';
import { getIpLocation } from './ipLocation';

// IMPORTANTE: En producción, usa variables de entorno y un proxy backend
const API_KEY = 'AIzaSyB17QgGs0V3BEvqLqyPh9t0WxL-Aa9ySpQ';
const TRANSLATE_API_URL = 'https://translation.googleapis.com/language/translate/v2';

class TranslateService {
  constructor() {
    this.cache = new Map();
    this.currentLanguage = localStorage.getItem('preferredLanguage') || 'es';
  }

  async translateText(text, targetLang) {
    // Verificar si el texto ya está traducido en cache
    const cacheKey = `${text}_${targetLang}`;
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Si el idioma objetivo es español, devolver el texto original
    if (targetLang === 'es') {
      return text;
    }

    try {
      const response = await axios.post(
        `${TRANSLATE_API_URL}?key=${API_KEY}`,
        {
          q: text,
          target: targetLang,
          source: 'es',
          format: 'text'
        }
      );

      const translatedText = response.data.data.translations[0].translatedText;
      
      // Guardar en cache
      this.cache.set(cacheKey, translatedText);
      
      return translatedText;
    } catch (error) {
      console.error('Error traduciendo:', error);
      return text; // Devolver texto original si hay error
    }
  }

  async translateBatch(texts, targetLang) {
    if (targetLang === 'es') {
      return texts;
    }

    try {
      const response = await axios.post(
        `${TRANSLATE_API_URL}?key=${API_KEY}`,
        {
          q: texts,
          target: targetLang,
          source: 'es',
          format: 'text'
        }
      );

      return response.data.data.translations.map(t => t.translatedText);
    } catch (error) {
      console.error('Error traduciendo batch:', error);
      return texts;
    }
  }

  // Elegir un idioma a mano desactiva el modo automático.
  setLanguage(lang) {
    this.currentLanguage = lang;
    localStorage.setItem('preferredLanguage', lang);
    localStorage.setItem('languageMode', 'manual');
  }

  // Modo automático: el idioma sigue al país del usuario. Es el modo por
  // defecto para quien nunca eligió idioma. Quien ya tenía un idioma guardado
  // de antes de existir languageMode se trata como elección manual, para no
  // cambiarle el idioma de golpe.
  isAutoLanguage() {
    const mode = localStorage.getItem('languageMode');
    if (mode) return mode === 'auto';
    return !localStorage.getItem('preferredLanguage');
  }

  // Activa el modo automático y aplica el idioma detectado. Devuelve true si
  // cambió el idioma (el caller recarga para aplicar traducciones).
  async setAutoLanguage() {
    localStorage.setItem('languageMode', 'auto');
    return this.autoDetectLanguageByCountry();
  }

  // En modo automático, detecta el idioma del país del usuario (por IP; si no
  // se puede, por el idioma del navegador) y lo aplica en cada carga, así si
  // el usuario cambia de país el idioma lo acompaña. Devuelve true si cambió
  // el idioma (para que el caller recargue la página y aplique traducciones).
  async autoDetectLanguageByCountry() {
    if (!this.isAutoLanguage()) return false;
    const location = await getIpLocation();
    const supported = new Set(Object.values(COUNTRY_LANGUAGE_MAP));
    const browserLang = (navigator.language || '').split('-')[0].toLowerCase();
    const detected =
      getLanguageForCountryCode(location?.countryCode) ||
      (supported.has(browserLang) ? browserLang : null);
    if (!detected) return false;
    localStorage.setItem('languageMode', 'auto');
    localStorage.setItem('preferredLanguage', detected);
    if (detected !== this.currentLanguage) {
      this.currentLanguage = detected;
      return true;
    }
    return false;
  }

  getLanguage() {
    return this.currentLanguage;
  }

  clearCache() {
    this.cache.clear();
  }
}

const translateService = new TranslateService();
export default translateService;